<template>
	<div class="toast-v2-container">
		<transition name="slide-in-right">
			<div
				v-if="showToast"
				:class="['toast-v2', localClassName]"
				:disabled="disabled"
				:variant="variant"
			>
				<div class="toast-v2__status-icon" v-if="useIcon">
					<i :class="'icon icon-' + localClassName.substring(10)"></i>
				</div>
				<div class="toast-v2__content">
					<span class="toast-v2__content-message">
						<slot>
							{{ message }}
						</slot>
					</span>
					<span
						class="toast-v2__content-timestamp"
						v-if="timestamp !== '' ? true : false"
					>
						{{ finalTimestampData }}
					</span>

					<div class="toast-v2__content-actions" v-if="useActions">
						<ButtonGroup :items="buttonItemsActions" />
					</div>
				</div>
				<Button
					class="toast-v2__close-btn"
					v-if="userDismissable"
					id="closeButton"
					className="btn--icon-only"
					:btnClick="closeWindow"
					:iconBeforeLabel="true"
					iconClassName="icon-close"
				/>
			</div>
		</transition>
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import ButtonGroup from '@/components/forms/button/button-group';

export default {
	name: 'ToastV2',
	components: {
		Button,
		ButtonGroup
	},
	props: {
		message: {
			type: String,
			default: ''
		},
		timestamp: {
			type: String,
			default: ''
		},
		finalTimestamp: {
			type: String,
			default: ''
		},
		className: {
			type: String,
			default: 'toast-v2__content-message'
		},
		timedDismissable: {
			type: Boolean,
			default: true
		},
		userDismissable: {
			type: Boolean,
			default: false
		},
		useIcon: {
			type: Boolean,
			default: false
		},
		useActions: {
			type: Boolean,
			default: false
		},
		statusCode: {
			type: Number,
			default: 0
		},
		toastId: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			showToast: false,
			buttonItemsActions: [
				{
					className: 'btn--link-alt',
					labelText: 'Dismiss',
					btnClick: this.closeWindow
				},
				{
					className: 'btn--link-alt',
					labelText: 'Snooze',
					btnClick: this.snooze
				}
			],
			finalTimestampData: this.finalTimestampData
		};
	},
	computed: {
		localClassName() {
			if (this.statusCode) {
				const x = this.statusCode;
				if (x >= 100 && x <= 199) return 'toast-v2--warning'; //todo
				if (x >= 200 && x <= 299) return 'toast-v2--success';
				if (x >= 400) return 'toast-v2--error';
			}
			return this.className;
		}
	},
	methods: {
		show() {
			this.showToast = true;

			this.setTimeout();
		},
		hide() {
			this.showToast = false;
		},
		snooze() {
			this.hide();

			setTimeout(() => {
				this.showToast = true;
			}, 5000);
		},
		setTimeout() {
			if (this.timedDismissable === true) {
				if (this.timer) {
					clearTimeout(this.timer);
					this.timer = null;
				}

				this.timer = setTimeout(() => {
					this.showToast = false;
				}, 6000);
			}
		},
		setTimestamp() {
			if (this.timestamp !== '') {
				const secondsThreshhold = 10000;
				const minutesThreshhold = 60000;
				const hoursThreshhold = 3600000;
				const daysThreshhold = 86400000;
				const timeDifference = Date.now() - new Date(this.timestamp);

				if (timeDifference < secondsThreshhold) {
					this.finalTimestampData = 'Now';
				} else if (
					timeDifference > secondsThreshhold &&
					timeDifference < minutesThreshhold
				) {
					this.finalTimestampData =
						Math.floor(timeDifference / secondsThreshhold) +
						' seconds ago';
				} else if (
					timeDifference > minutesThreshhold &&
					timeDifference < hoursThreshhold
				) {
					this.finalTimestampData =
						Math.floor(timeDifference / minutesThreshhold) +
						' minutes ago';
				} else if (
					timeDifference > hoursThreshhold &&
					timeDifference < daysThreshhold
				) {
					this.finalTimestampData =
						Math.floor(timeDifference / hoursThreshhold) +
						' hours ago';
				} else {
					this.finalTimestampData =
						Math.floor(timeDifference / daysThreshhold) +
						' days ago';
				}
			}
		},
		closeWindow() {
			this.hide();
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * [Description]
    *
    * Toast V2 Notifications
    * Notification Color Variations and icons
    * Can use SCSS variables = TRUE
 */

.toast-v2 {
	display: inline-flex;
	justify-content: space-between;
	max-width: 350px;
	padding: $padding-sm;
	background-color: $ui-neutral-light;
	border: 2px solid $ui-neutral;
	border-radius: $notify-border-radius;
	border-bottom-width: $notify-bottom-border;
	margin-bottom: $gutter-md;
	@include text-contrast($ui-neutral-light, $white, $black);
	box-shadow: 0px 3px 4px rgba($neutral-3, 60%);

	&__status-icon {
		font-size: $default-font-size + 6;
		margin-right: $gutter-lg;
	}

	&__content {
		&-message {
			font-size: $default-font-size - 4;
			align-self: center;
			display: inline-flex;
		}

		&-timestamp {
			font-size: $default-font-size - 4;
			color: $neutral-2;
			margin: $gutter-lg 0 $gutter-md;
		}
	}

	&__close-btn {
		margin-left: $gutter-lg;
	}

	&--success {
		background-color: $ui-positive-light;
		border-color: $ui-positive;
		@include text-contrast($ui-positive-light, $white, $black);
	}

	&--warning {
		background-color: $ui-warning-light;
		border-color: $ui-warning;
		@include text-contrast($ui-warning-light, $white, $black);
	}

	&--error {
		background-color: $ui-negative-light;
		border-color: $ui-negative;
		@include text-contrast($ui-negative-light, $white, $black);
	}
}

@include transition-in-right;
</style>
